import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { AdminListCard } from "@components/CatalogIndex/AdminListCard";
import { useV2DeleteNoteMutation } from "@generated";
import useListNotes from "../api/useListNotes";
import { Note } from "../types/Note";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  projectStatus: string;
  permission: string;
}

const Show: React.FC<Props> = ({ projectId, projectStatus, permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteNote] = useV2DeleteNoteMutation();

  const { notes, notesLoading, notesFetching, pageMeta, refetch } =
    useListNotes({
      projectId: Number(projectId),
      page: { number: pageNumber, size: pageSize },
    });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Note updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Note created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const handleDelete = () => {
    setDeleteQueue([...selectedNoteIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    const promises = deleteQueue.map((noteId) =>
      deleteNote({
        projectId: String(projectId),
        id: String(noteId),
      })
    );

    Promise.all(promises)
      .then(() => {
        setSuccessMessage(
          `Note${deleteQueue.length > 1 ? "s" : ""} deleted successfully!`
        );
        setShowSuccessAlert(true);
        setIsDeleting(false);
        setShowConfirmationModal(false);
        setSelectedNoteIds([]);
        setDeleteQueue([]);
        refetch();
      })
      .catch(() => {
        setErrorMessages(
          "One or more notes could not be deleted. Please try again later."
        );
        setShowFailAlert(true);
        setIsDeleting(false);
      });
  };

  const getConfirmationMessage = () => (
    <div style={{ color: "#1C2655", fontSize: "14px" }}>
      {isDeleting ? (
        <SmallSpinner />
      ) : (
        <span style={{ fontWeight: "700" }}>
          {`You are about to delete ${selectedNoteIds.length} note${
            selectedNoteIds.length > 1 ? "s" : ""
          }.`}
        </span>
      )}
      <ul style={{ marginTop: "10px" }}>
        {selectedNoteIds.map((id) => {
          const noteName = notes.find((n) => n.id === id)?.name;
          return <li key={id}>{noteName}</li>;
        })}
      </ul>
      This action cannot be undone. Do you wish to proceed?
    </div>
  );

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const columns: ColumnDef<Note>[] = [
    {
      header: "Note Name",
      cell(props) {
        const note = props.row.original;
        if (note.id === "0") {
          return (
            <div
              className={styles.editLink}
              onClick={() => {
                navigate(`/projects/${projectId}/notes/new`, {
                  state: {},
                });
              }}
            >
              {note.name}
            </div>
          );
        }
        return (
          <div
            className={styles.editLink}
            onClick={() => {
              navigate(`/projects/${projectId}/notes/edit/${note.id}`, {
                state: { note },
              });
            }}
          >
            {note.name} <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Note Content",
      cell(props) {
        return props.row.original.content;
      },
    },
    {
      header: "Created By",
      cell(props) {
        return props.row.original.username;
      },
    },
    {
      header: "Created On",
      cell(props) {
        return formatDate(props.row.original.createdAt);
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className={styles.page}>
        You do not have permission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminListCard
        title="Notes"
        onNew={
          permission !== "view" && projectStatus === "building"
            ? () =>
                navigate(`/projects/${projectId}/notes/new`, {
                  state: {},
                })
            : undefined
        }
        newButtonLabel="Add Note"
        batchActions={[
          {
            icon: faTrashCan,
            label: "Delete",
            onClick: handleDelete,
            variant: "danger",
          },
        ]}
        selectedCount={selectedNoteIds.length}
      >
        {notesLoading || notesFetching ? (
          <ScopeStackSpinner />
        ) : (
          <DataTable
            data={
              notes.length > 0
                ? notes
                : [
                    {
                      id: "0",
                      name: "Add notes to get started",
                      content: "",
                      username: "",
                      createdAt: "",
                      editable: false,
                    },
                  ]
            }
            columns={columns}
            selectable={
              notes.length > 0 &&
              permission !== "view" &&
              projectStatus === "building"
            }
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            paginationEnabled
            pageSizeEnabled
            selectedIds={selectedNoteIds}
            setSelectedIds={setSelectedNoteIds}
          />
        )}
        <ScopeStackModal
          modalTitle="Delete Note"
          modalBody={getConfirmationMessage()}
          handleButton2Click={isDeleting ? () => null : confirmDelete}
          handleClose={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          show={showConfirmationModal}
          handleButton1Click={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          deleteModal={true}
          button1Text="Cancel"
          button2Text="Delete"
        />
      </AdminListCard>
    </div>
  );
};

export default Show;
